import { useCallback, useEffect, useMemo, useState } from "react";

import { Alert, Button } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { DateTime } from "luxon";
import type { CurrencyCode } from "@doitintl/cmp-models";

import { googleSheetsExportDialogText } from "../../../../assets/texts";
import useMountEffect from "../../../../Components/hooks/useMountEffect";
import LoadingButton from "../../../../Components/LoadingButton";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { Scopes, useGapiContext } from "../../../../Context/GapiContext";
import { consoleErrorWithSentry } from "../../../../utils";
import {
  arraySections,
  buildArraySectionRows,
  buildNumberSectionRows,
  buildObjectSectionRows,
  buildSectionLabelRow,
  emptyLine,
  getCurrencyCellFormat,
  getSectionDefs,
  getSectionTotalKey,
  getSectionTotalLabel,
  LABELS,
  numberSections,
  objectSections,
} from "./utils";
import type { Explainer, ExplainerSummary } from "../types";

type Props = {
  onClose: () => void;
  isDialogOpen?: boolean;
  isDialog?: boolean;
  explainer: Explainer;
  currency: CurrencyCode;
};

const ExportToGoogleSheetsDialog = ({ explainer, isDialog, isDialogOpen, onClose, currency }: Props) => {
  const [loading, setLoading] = useState(false);
  const [lastExportFailedReason, setLastExportFailedReason] = useState<string>();
  const { getToken, isAuthenticated } = useGapiContext();
  const { customer } = useCustomerContext();

  const currencyCellFormat = getCurrencyCellFormat(currency);

  useMountEffect(() => {
    getToken(Scopes.DriveFile);
  });

  useEffect(() => {
    setLoading(false);
  }, [isDialogOpen]);

  const sectionDefs = useMemo(() => getSectionDefs(explainer), [explainer]);

  const buildRows = useCallback(
    (
      headers: string[],
      awsSummary: ExplainerSummary,
      doitSummary: ExplainerSummary,
      section: keyof ExplainerSummary
    ): gapi.client.sheets.RowData[] => {
      const buildInvoiceSection = (
        label: string,
        summary: ExplainerSummary,
        section: keyof ExplainerSummary,
        headers: string[]
      ): gapi.client.sheets.RowData[] => {
        const rows: gapi.client.sheets.RowData[] = [];
        const sectionData = summary[section];

        rows.push(buildSectionLabelRow(label));

        if (arraySections.includes(section)) {
          rows.push({
            values: headers.map((header) => ({
              userEnteredValue: { stringValue: LABELS[header] || header },
            })),
          });
          rows.push(...buildArraySectionRows(sectionData as Record<string, unknown>[], headers, currencyCellFormat));
        } else if (objectSections.includes(section)) {
          rows.push(...buildObjectSectionRows(sectionData as Record<string, unknown>, currencyCellFormat));
        } else if (numberSections.includes(section)) {
          rows.push(...buildNumberSectionRows(sectionData as number, LABELS[section] || section, currencyCellFormat));
        }

        const totalKey = getSectionTotalKey(section);
        const totalLabel = getSectionTotalLabel(section);

        if (totalKey && summary[totalKey] !== undefined) {
          rows.push(emptyLine);
          rows.push(...buildNumberSectionRows(summary[totalKey], totalLabel || "Total", currencyCellFormat));
        }
        return rows;
      };

      const awsRows = buildInvoiceSection("AWS' invoice to DoiT", awsSummary, section, headers);
      const doitRows = buildInvoiceSection("DoiT's invoice to you", doitSummary, section, headers);
      return awsRows.concat([emptyLine], doitRows);
    },
    [currencyCellFormat]
  );

  const hasData = useMemo(() => sectionDefs.some((def) => def.headers.length), [sectionDefs]);

  const handleExport = useCallback(async () => {
    setLoading(true);
    await getToken(Scopes.DriveFile);
    const timestamp = DateTime.local().toISO({ includeOffset: false });
    const sheets = sectionDefs.map(({ key, label, headers }) => ({
      properties: { title: label || key },
      data: [
        {
          startRow: 0,
          startColumn: 0,
          rowData: buildRows(headers, explainer.summary.aws, explainer.summary.doit, key as keyof ExplainerSummary),
          columnMetadata: headers.map(() => ({ pixelSize: 200 })),
        },
      ],
    }));
    const spreadsheetBody: gapi.client.sheets.Spreadsheet = {
      properties: {
        title: `${customer.name.replace(/\s/g, "-")}-invoice-explainer-${timestamp}`,
      },
      sheets,
    };
    try {
      const response = await gapi.client.sheets.spreadsheets.create({}, spreadsheetBody);
      if (response.result.spreadsheetUrl) {
        setLastExportFailedReason(undefined);
        const win = window.open(response.result.spreadsheetUrl, "_blank");
        if (win !== null) {
          win.focus();
        }
      }
      onClose();
    } catch (err: unknown) {
      let message = "Unknown error";
      let sentryError: unknown = err;
      if (typeof err === "object" && err !== null) {
        const maybeMessage =
          (err as { result?: { error?: { message?: string } } })?.result?.error?.message ?? (err as Error)?.message;
        if (typeof maybeMessage === "string") {
          message = maybeMessage;
          sentryError = (err as { result?: { error?: unknown } })?.result?.error ?? err;
        }
      }
      setLastExportFailedReason(message);
      consoleErrorWithSentry(sentryError);
    } finally {
      setLoading(false);
    }
  }, [customer.name, explainer, buildRows, getToken, onClose, sectionDefs]);

  const dialogData = useMemo(() => {
    if (isAuthenticated(Scopes.DriveFile)) {
      return {
        content: googleSheetsExportDialogText.DIALOG_SAVE,
        actionButtonText: googleSheetsExportDialogText.DIALOG_EXPORT,
        actionCallback: handleExport,
      };
    }
    return {
      content: googleSheetsExportDialogText.SELECT_ACCOUNT,
      actionButtonText: googleSheetsExportDialogText.DIALOG_ALLOW,
      actionCallback: getToken,
    };
  }, [getToken, handleExport, isAuthenticated]);

  const alertUI = useCallback(
    ({ content, actionButtonText, actionCallback }) => {
      const getSeverity = () => {
        if (lastExportFailedReason === undefined) {
          return "info";
        }

        return lastExportFailedReason ? "success" : "error";
      };
      return (
        <Alert
          variant="standard"
          severity={getSeverity()}
          action={
            <>
              <LoadingButton
                color="inherit"
                size="small"
                variant="outlined"
                onClick={actionCallback}
                loading={loading}
                disabled={loading}
                data-cy="export-to-google-sheets-alert-action"
                mixpanelEventId={`invoices.renderer.google-sheets.${actionButtonText}`}
              >
                {actionButtonText}
              </LoadingButton>
            </>
          }
        >
          {content}
        </Alert>
      );
    },
    [lastExportFailedReason, loading]
  );

  const dialogUI = useCallback(
    ({ content, actionButtonText, actionCallback }) => (
      <Dialog open={isDialogOpen ?? false} onClose={onClose}>
        <DialogTitle>{googleSheetsExportDialogText.DIALOG_TITLE}</DialogTitle>
        <DialogContent>
          {!!lastExportFailedReason && (
            <Alert variant="standard" severity="error">
              {lastExportFailedReason}
            </Alert>
          )}
          <DialogContentText>
            <Typography component="span">{content}</Typography>
          </DialogContentText>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={onClose} variant="text" data-cy="export-to-google-sheets-cancel">
            {googleSheetsExportDialogText.DIALOG_CANCEL}
          </Button>
          <LoadingButton
            color="primary"
            onClick={actionCallback}
            disabled={loading}
            loading={loading}
            variant="contained"
            data-cy="export-to-google-sheets-action"
            mixpanelEventId={`analytics.renderer.google-sheets.${actionButtonText}`}
          >
            {actionButtonText}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    ),
    [isDialogOpen, lastExportFailedReason, loading, onClose]
  );
  if (!hasData || !isAuthenticated(Scopes.DriveFile)) {
    return null;
  }

  return isDialog ? dialogUI(dialogData) : alertUI(dialogData);
};

export default ExportToGoogleSheetsDialog;
